import React, { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import discoverTalent from '@assets/discover-talent.png';

import {
  EmptyAvailableCandidateFilters,
  IAvailableCandidatesViewState,
} from '@hooks';
import {
  AvailableFilterOptions,
  formatSkillLabel,
  IAvailableCandidateFilters,
  isSenioritySelected,
} from '@utils';
import { touchTargetSx } from 'theme/touchTarget';
import { offersChoice } from 'components/candidate/CandidatesList/components/CandidateFilterSections';
import { CardField } from './CardField';
import { SeniorityRangeField } from './SeniorityRangeField';

const selectedRange = (scale: string[], selected: string[]): number[] => {
  const picked = scale.flatMap((seniority, index) =>
    isSenioritySelected(selected, seniority) ? [index] : [],
  );

  return picked.length
    ? [picked[0], picked[picked.length - 1]]
    : [0, scale.length - 1];
};

type CardDraft = Pick<
  IAvailableCandidateFilters,
  'regions' | 'primarySkills' | 'skills'
>;

interface IAvailableCandidatesSearchFormProps {
  options: AvailableFilterOptions;
  filters: IAvailableCandidateFilters;
  onApply: (patch: Partial<IAvailableCandidatesViewState>) => void;
}

export const AvailableCandidatesSearchForm: React.FC<
  IAvailableCandidatesSearchFormProps
> = ({ options, filters, onApply }) => {
  const scale = options.seniorityScale;
  const [touchedRange, setRange] = useState<number[]>();
  const range = touchedRange ?? selectedRange(scale, filters.seniorities);
  const [touched, setTouched] = useState<Partial<CardDraft>>({});
  const draft: CardDraft = {
    regions: filters.regions,
    primarySkills: filters.primarySkills,
    skills: filters.skills,
    ...touched,
  };

  const change = (patch: Partial<CardDraft>) =>
    setTouched({ ...touched, ...patch });

  const apply = () =>
    onApply({
      query: '',
      ...EmptyAvailableCandidateFilters,
      ...draft,
      seniorities: scale.slice(range[0], range[1] + 1),
    });

  return (
    <Stack
      alignItems="center"
      width="100%"
      maxWidth="39.3125rem"
      mx="auto"
      my={{ xs: 2, tablet: 4 }}
      px={2}
      pb={5.75}
      bgcolor="common.white"
      borderRadius={1.5}
      boxShadow={2}
    >
      <Box
        component="img"
        src={discoverTalent}
        alt=""
        width="100%"
        maxWidth="22.9375rem"
      />
      <Typography variant="h2" textAlign="center">
        Discover best talent with AgileEngine
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        With 100+ ready to hire talent and people from the bench
      </Typography>

      <Stack gap={2} width="100%" maxWidth="20.25rem" pt={4}>
        {offersChoice(scale.length) && (
          <SeniorityRangeField
            scale={scale}
            value={range}
            onChange={setRange}
          />
        )}

        <CardField
          label="Location"
          placeholder="Any location"
          options={options.regions}
          value={draft.regions}
          onChange={(regions) => change({ regions })}
        />

        <CardField
          label="Stack"
          placeholder="All stacks"
          options={options.primarySkills}
          value={draft.primarySkills}
          getOptionLabel={formatSkillLabel}
          onChange={(primarySkills) => change({ primarySkills })}
        />

        <CardField
          label="Skills"
          placeholder="Any skills"
          options={options.skills}
          value={draft.skills}
          getOptionLabel={formatSkillLabel}
          onChange={(skills) => change({ skills })}
        />
      </Stack>

      <Button
        variant="contained"
        disableElevation={false}
        disabled={[draft.regions, draft.primarySkills, draft.skills].some(
          ({ length }) => !length,
        )}
        onClick={apply}
        sx={{ mt: 2.75, ...touchTargetSx }}
      >
        View candidates
      </Button>
    </Stack>
  );
};
